// src/components/expert/ImportOffersPdf.jsx
// Déposer un PDF reçu d'une galerie (preview de foire ou d'exposition), relire les œuvres lues, puis les ajouter à une
// proposition. Rien n'est créé avant la validation ; les œuvres ajoutées restent des cibles, jamais des biens détenus.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { VISION_API_URL } from "../../config/domains";
import { offerKey, offerToArtwork, offerToRecord, defaultSelection } from "../../utils/offerImport";

const MAX_BYTES = 30 * 1024 * 1024;
const card = { background: "#fff", border: "1px solid #e5e3df", borderRadius: 4, padding: 16 };
const btn = { padding: "8px 14px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 2, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "system-ui, sans-serif" };
const btnGhost = { ...btn, background: "#fff", color: "#1a1a1a", border: "1px solid #1a1a1a" };
const input = { padding: "5px 8px", border: "1px solid #e5e3df", borderRadius: 2, fontSize: 12.5, fontFamily: "system-ui, sans-serif", width: "100%", boxSizing: "border-box" };
const small = { fontSize: 12, color: "#6b6b6b", fontFamily: "system-ui, sans-serif" };

const COPY = {
  fr: {
    title: "Importer un PDF de galerie",
    intro: "Déposez un PDF reçu d'une galerie ou d'une foire : ARTICHAUT lit les œuvres, leurs prix et leurs images. Vous relisez avant d'ajouter à la proposition. Les œuvres restent des cibles : jamais un bien détenu.",
    pick: "Choisir un PDF", reading: "Lecture du document…",
    tooBig: "Ce fichier dépasse 30 Mo.", notPdf: "Seuls les PDF sont acceptés.",
    failed: (m) => `Lecture impossible : ${m}`, noOffers: "Aucune œuvre reconnue dans ce document (légende avec dimensions attendue). Les pages de texte et de titre sont ignorées.",
    found: (n, p, r) => `${n} œuvre(s) lue(s) · ${p} avec un prix · ${r} à vérifier`,
    colPick: "", colWork: "Œuvre", colDims: "Dimensions", colRef: "Réf. galerie", colPrice: "Prix de la galerie", colPage: "Page", colCheck: "À vérifier",
    already: "Déjà dans la proposition",
    price: { on_request: "Sur demande", sold: "Vendu", reserved: "Réservé", not_stated: "Non indiqué" },
    tax: { excl: "HT", incl: "TTC", none: "HT/TTC non précisé" },
    warn: {
      missing_title: "Titre absent", missing_artist: "Artiste absent", dims_unparsed: "Dimensions illisibles", inches_only: "Dimensions en pouces seulement",
      price_missing: "Prix non indiqué", tax_unspecified: "Mention HT/TTC absente du document", currency_unknown: "Devise non reconnue",
      image_uncertain: "Image à confirmer", no_image: "Image non trouvée", duplicate_reference: "Même référence que d'autres œuvres du document", conflicting_duplicates: "Prix différents pour une même œuvre",
    },
    selectAll: "Tout sélectionner", selectNone: "Tout désélectionner", skipped: (n) => `${n} page(s) ignorée(s) (texte, titres, images seules).`,
    add: (n) => `Ajouter ${n} œuvre(s) à la proposition`, cancel: "Annuler", saving: (c, t) => `Ajout en cours… ${c} / ${t}`,
    done: (n, a, e) => `✓ ${n} œuvre(s) ajoutée(s)${a ? `, ${a} déjà présente(s)` : ""}${e ? `, ${e} en erreur` : ""}.`,
    again: "Importer un autre PDF", close: "Fermer", noteGallery: "Les prix lus sont les prix de la galerie. Le prix montré au client se règle ensuite, œuvre par œuvre, en HT ou en TTC.",
  },
  en: {
    title: "Import a gallery PDF",
    intro: "Drop a PDF received from a gallery or fair: ARTICHAUT reads the works, their prices and images. You review before adding to the proposal. Works stay targets: never an owned asset.",
    pick: "Choose a PDF", reading: "Reading the document…",
    tooBig: "This file is over 30 MB.", notPdf: "Only PDF files are accepted.",
    failed: (m) => `Could not read: ${m}`, noOffers: "No work recognised in this document (a caption with dimensions is expected). Text and title pages are ignored.",
    found: (n, p, r) => `${n} work(s) read · ${p} with a price · ${r} to check`,
    colPick: "", colWork: "Work", colDims: "Dimensions", colRef: "Gallery ref.", colPrice: "Gallery price", colPage: "Page", colCheck: "To check",
    already: "Already in the proposal",
    price: { on_request: "On request", sold: "Sold", reserved: "Reserved", not_stated: "Not stated" },
    tax: { excl: "excl. tax", incl: "incl. tax", none: "tax not specified" },
    warn: {
      missing_title: "Title missing", missing_artist: "Artist missing", dims_unparsed: "Dimensions unreadable", inches_only: "Dimensions in inches only",
      price_missing: "Price not stated", tax_unspecified: "Tax mention missing from the document", currency_unknown: "Currency not recognised",
      image_uncertain: "Image to confirm", no_image: "Image not found", duplicate_reference: "Same reference as other works in the document", conflicting_duplicates: "Different prices for the same work",
    },
    selectAll: "Select all", selectNone: "Select none", skipped: (n) => `${n} page(s) skipped (text, titles, images only).`,
    add: (n) => `Add ${n} work(s) to the proposal`, cancel: "Cancel", saving: (c, t) => `Adding… ${c} / ${t}`,
    done: (n, a, e) => `✓ ${n} work(s) added${a ? `, ${a} already present` : ""}${e ? `, ${e} failed` : ""}.`,
    again: "Import another PDF", close: "Close", noteGallery: "The prices read are the gallery's prices. The price shown to the client is then set work by work, excl. or incl. tax.",
  },
  de: {
    title: "Galerie-PDF importieren",
    intro: "Legen Sie ein PDF einer Galerie oder Messe ab: ARTICHAUT liest die Werke, ihre Preise und Bilder. Sie prüfen vor dem Hinzufügen zum Angebot. Die Werke bleiben Zielwerke: nie ein Besitz.",
    pick: "PDF auswählen", reading: "Dokument wird gelesen…",
    tooBig: "Diese Datei ist größer als 30 MB.", notPdf: "Nur PDF-Dateien werden akzeptiert.",
    failed: (m) => `Lesen nicht möglich: ${m}`, noOffers: "Kein Werk in diesem Dokument erkannt (Bildunterschrift mit Maßen erwartet). Text- und Titelseiten werden ignoriert.",
    found: (n, p, r) => `${n} Werk(e) gelesen · ${p} mit Preis · ${r} zu prüfen`,
    colPick: "", colWork: "Werk", colDims: "Maße", colRef: "Galerie-Ref.", colPrice: "Galeriepreis", colPage: "Seite", colCheck: "Zu prüfen",
    already: "Bereits im Angebot",
    price: { on_request: "Auf Anfrage", sold: "Verkauft", reserved: "Reserviert", not_stated: "Nicht angegeben" },
    tax: { excl: "netto", incl: "brutto", none: "Steuer nicht angegeben" },
    warn: {
      missing_title: "Titel fehlt", missing_artist: "Künstler fehlt", dims_unparsed: "Maße nicht lesbar", inches_only: "Maße nur in Zoll",
      price_missing: "Preis nicht angegeben", tax_unspecified: "Steuerhinweis fehlt im Dokument", currency_unknown: "Währung nicht erkannt",
      image_uncertain: "Bild bitte bestätigen", no_image: "Bild nicht gefunden", duplicate_reference: "Gleiche Referenz wie andere Werke im Dokument", conflicting_duplicates: "Unterschiedliche Preise für dasselbe Werk",
    },
    selectAll: "Alle auswählen", selectNone: "Auswahl aufheben", skipped: (n) => `${n} Seite(n) übersprungen (Text, Titel, nur Bilder).`,
    add: (n) => `${n} Werk(e) zum Angebot hinzufügen`, cancel: "Abbrechen", saving: (c, t) => `Wird hinzugefügt… ${c} / ${t}`,
    done: (n, a, e) => `✓ ${n} Werk(e) hinzugefügt${a ? `, ${a} bereits vorhanden` : ""}${e ? `, ${e} fehlgeschlagen` : ""}.`,
    again: "Weiteres PDF importieren", close: "Schließen", noteGallery: "Die gelesenen Preise sind die Preise der Galerie. Der dem Kunden gezeigte Preis wird anschließend je Werk festgelegt, netto oder brutto.",
  },
};

const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = () => reject(new Error("read"));
  r.readAsDataURL(file);
});
const b64ToBlob = (b64, type) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
};
const fmtPrice = (p, c) => {
  if (!p) return "—";
  if (p.status !== "amount") return c.price[p.status] || "—";
  const tax = p.tax === "excl" ? c.tax.excl : p.tax === "incl" ? c.tax.incl : c.tax.none;
  return `${Number(p.amount).toLocaleString()} ${p.currency || "?"} · ${tax}`;
};

export default function ImportOffersPdf({ collectionId, onDone }) {
  const { user } = useAuth() || {};
  const { i18n } = useTranslation();
  const lang = String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2);
  const c = COPY[lang] || COPY.fr;

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("idle"); // idle | reading | review | saving | done
  const [error, setError] = useState("");
  const [data, setData] = useState(null);   // réponse de /extract-offers
  const [edits, setEdits] = useState({});   // clé d'offre -> { artist, title }
  const [selected, setSelected] = useState(new Set());
  const [existing, setExisting] = useState(new Set());
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [result, setResult] = useState(null);

  // Œuvres déjà ajoutées depuis un PDF à cette proposition : ne pas les recréer.
  useEffect(() => {
    let alive = true;
    if (!open || !collectionId) return undefined;
    supabase.from("artworks").select("external_id").eq("collection_id", collectionId).eq("external_source", `pdf_offer:${collectionId}`)
      .then(({ data: rows }) => { if (alive) setExisting(new Set((rows || []).map((r) => String(r.external_id).toLowerCase()))); });
    return () => { alive = false; };
  }, [open, collectionId, step]);

  const alreadyIn = (o) => existing.has(String(o.external_reference ? o.external_reference.trim() : offerKey(o)).toLowerCase());

  async function handleFile(file) {
    if (!file) return;
    setError(""); setResult(null);
    if (!/\.pdf$/i.test(file.name) && file.type !== "application/pdf") { setError(c.notPdf); return; }
    if (file.size > MAX_BYTES) { setError(c.tooBig); return; }
    setStep("reading");
    try {
      const [{ data: { session } }, b64] = await Promise.all([supabase.auth.getSession(), toBase64(file)]);
      const token = session?.access_token;
      const res = await fetch(`${VISION_API_URL}/extract-offers`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ file: b64, filename: file.name, with_images: true }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setData({ ...body, source: { ...body.source, filename: file.name, extractor_version: body.extractor_version } });
      setEdits({});
      setSelected(defaultSelection(body.offers));
      setStep("review");
    } catch (e) {
      setError(c.failed(e?.message || "?"));
      setStep("idle");
    }
  }

  const val = (o, field) => (edits[offerKey(o)]?.[field] ?? o[field] ?? "");
  const setEdit = (o, field, v) => setEdits((prev) => ({ ...prev, [offerKey(o)]: { ...prev[offerKey(o)], [field]: v } }));
  const toggle = (o) => setSelected((prev) => { const n = new Set(prev); const k = offerKey(o); n.has(k) ? n.delete(k) : n.add(k); return n; });

  async function save() {
    if (!data || !user?.id) return;
    const todo = data.offers.filter((o) => selected.has(offerKey(o)) && !alreadyIn(o));
    setStep("saving");
    let created = 0, already = 0, failed = 0;
    for (let i = 0; i < todo.length; i += 1) {
      setProgress({ current: i + 1, total: todo.length });
      const base = todo[i];
      const o = { ...base, artist: val(base, "artist") || null, title: val(base, "title") || base.title };
      try {
        let imageUrl = null;
        const thumb = o.image?.thumbnail_b64;
        if (thumb) {
          const path = `${user.id}/offers/${Date.now()}-${Math.random().toString(36).slice(2)}.jpg`;
          const up = await supabase.storage.from("artworks-images").upload(path, b64ToBlob(thumb, "image/jpeg"), { contentType: "image/jpeg" });
          if (!up.error) imageUrl = supabase.storage.from("artworks-images").getPublicUrl(path).data.publicUrl;
        }
        const row = offerToArtwork(o, { clientId: user.id, collectionId, imageUrl, issuer: data.source?.issuer_hint, filename: data.source?.filename });
        const { data: art, error: aErr } = await supabase.from("artworks").insert(row).select("id").single();
        if (aErr) { if (aErr.code === "23505") { already += 1; continue; } throw aErr; }
        await supabase.from("proposal_offers").insert({ ...offerToRecord(o, { collectionId, advisorId: user.id, source: data.source }), artwork_id: art.id });
        await supabase.from("proposal_items").upsert(
          { collection_id: collectionId, artwork_id: art.id, currency: o.price?.currency || "EUR", position: i, updated_at: new Date().toISOString() },
          { onConflict: "artwork_id" }
        );
        created += 1;
      } catch (e) {
        console.error("[ImportOffersPdf]", e);
        failed += 1;
      }
    }
    setResult({ created, already, failed });
    setStep("done");
    onDone?.();
  }

  const reset = () => { setStep("idle"); setData(null); setResult(null); setError(""); setSelected(new Set()); };
  const offers = data?.offers || [];
  const chosen = offers.filter((o) => selected.has(offerKey(o)) && !alreadyIn(o)).length;

  return (
    <div style={card}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 14, fontWeight: 600, fontFamily: "system-ui, sans-serif" }}>
        {open ? "▾" : "▸"} {c.title}
      </button>
      {open && (
        <div style={{ marginTop: 12 }}>
          <p style={{ ...small, maxWidth: 680, lineHeight: 1.6 }}>{c.intro}</p>
          {error && <p style={{ ...small, color: "#8f3a44" }}>{error}</p>}

          {(step === "idle" || step === "reading") && (
            <label style={{ ...btnGhost, display: "inline-block", opacity: step === "reading" ? 0.6 : 1 }}>
              <input type="file" accept="application/pdf,.pdf" style={{ display: "none" }} disabled={step === "reading"} onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }} />
              {step === "reading" ? c.reading : c.pick}
            </label>
          )}

          {step === "review" && data && (
            <div>
              <p style={{ ...small, color: "#1a1a1a" }}>
                {c.found(offers.length, data.counts?.with_price ?? 0, data.counts?.to_review ?? 0)}
                {data.source?.issuer_hint ? ` · ${data.source.issuer_hint}` : ""}
              </p>
              {offers.length === 0 && <p style={small}>{c.noOffers}</p>}
              {offers.length > 0 && (
                <>
                  <p style={small}>{c.noteGallery}</p>
                  <div style={{ display: "flex", gap: 10, margin: "6px 0" }}>
                    <button style={btnGhost} onClick={() => setSelected(new Set(offers.filter((o) => !alreadyIn(o)).map(offerKey)))}>{c.selectAll}</button>
                    <button style={btnGhost} onClick={() => setSelected(new Set())}>{c.selectNone}</button>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, fontFamily: "system-ui, sans-serif" }}>
                      <thead>
                        <tr style={{ textAlign: "left", color: "#8b7355" }}>
                          <th /><th /><th>{c.colWork}</th><th>{c.colDims}</th><th>{c.colRef}</th><th>{c.colPrice}</th><th>{c.colPage}</th><th>{c.colCheck}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {offers.map((o) => {
                          const k = offerKey(o);
                          const done = alreadyIn(o);
                          return (
                            <tr key={k} style={{ borderTop: "1px solid #f0eee9", verticalAlign: "top", opacity: done ? 0.5 : 1 }}>
                              <td style={{ padding: "6px 6px 6px 0" }}><input type="checkbox" disabled={done} checked={selected.has(k) && !done} onChange={() => toggle(o)} /></td>
                              <td style={{ padding: 6 }}>
                                {o.image?.thumbnail_b64
                                  ? <img alt="" src={`data:image/jpeg;base64,${o.image.thumbnail_b64}`} style={{ width: 54, height: 54, objectFit: "cover", borderRadius: 2 }} />
                                  : <div style={{ width: 54, height: 54, background: "#f3f0ea", borderRadius: 2 }} />}
                              </td>
                              <td style={{ padding: 6, minWidth: 200 }}>
                                <input style={{ ...input, marginBottom: 4 }} value={val(o, "artist")} placeholder={c.warn.missing_artist} onChange={(e) => setEdit(o, "artist", e.target.value)} />
                                <input style={input} value={val(o, "title")} placeholder={c.warn.missing_title} onChange={(e) => setEdit(o, "title", e.target.value)} />
                                <div style={small}>{[o.year, o.medium].filter(Boolean).join(" · ")}</div>
                              </td>
                              <td style={{ padding: 6 }}>{o.dimensions_cm ? `${[o.dimensions_cm.height, o.dimensions_cm.width, o.dimensions_cm.depth].filter((v) => v != null).join(" × ")} cm` : (o.dimensions_raw || "—")}{o.edition ? <div style={small}>{o.edition}</div> : null}</td>
                              <td style={{ padding: 6 }}>{o.external_reference || "—"}</td>
                              <td style={{ padding: 6 }}>{fmtPrice(o.price, c)}</td>
                              <td style={{ padding: 6 }}>{(o.pages || []).join(", ")}</td>
                              <td style={{ padding: 6, color: "#a06010" }}>
                                {done ? c.already : (o.warnings || []).filter((w) => c.warn[w]).map((w) => <div key={w}>{c.warn[w]}</div>)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {(data.skipped_pages || []).length > 0 && <p style={small}>{c.skipped(data.skipped_pages.length)}</p>}
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button style={{ ...btn, opacity: chosen ? 1 : 0.5 }} disabled={!chosen} onClick={save}>{c.add(chosen)}</button>
                <button style={btnGhost} onClick={reset}>{c.cancel}</button>
              </div>
            </div>
          )}

          {step === "saving" && <p style={small}>{c.saving(progress.current, progress.total)}</p>}

          {step === "done" && result && (
            <div>
              <p style={{ ...small, color: "#2c5f2d", fontSize: 13 }}>{c.done(result.created, result.already, result.failed)}</p>
              <div style={{ display: "flex", gap: 10 }}>
                <button style={btnGhost} onClick={reset}>{c.again}</button>
                <button style={btn} onClick={() => { reset(); setOpen(false); }}>{c.close}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
